import * as THREE from "three";
import { GameState } from "@/GameState";
import { OdysseyModelControllerType } from "@/enums/odyssey/OdysseyModelControllerType";
import { TextureLoader } from "@/loaders/TextureLoader";
import { OdysseyModelNode, OdysseyModelNodeEmitter } from "@/odyssey";
import type { OdysseyController } from "@/odyssey/controllers";
import { OdysseyObject3D } from "@/three/odyssey/OdysseyObject3D";
import {
  ODYSSEY_AREA_WIND_DIRECTION,
  resolveDanglyWindPower,
} from "@/three/odyssey/windPowerContext";

/**
 * OdysseyEmitter3D class.
 * 
 * THREE.js representation of OdysseyEmitter.
 * Handles all emitter render modes, update modes, P2P gravity/bezier,
 * bounce, splat, wind, tinting, chunk emitters, trail spawning, and
 * all inheritance flags per the NWN MDL emitter specification.
 * 
 * KotOR JS - A remake of the Odyssey Game Engine that powered KotOR I & II
 * 
 * @file OdysseyEmitter3D.ts
 * @author KobaltBlu <https://github.com/KobaltBlu>
 * @license {@link https://www.gnu.org/licenses/gpl-3.0.txt|GPLv3}
 */
export class OdysseyEmitter3D extends OdysseyObject3D {

  // Preallocated scratch objects to avoid per-frame heap allocations
  private static _v3A = new THREE.Vector3();
  private static _v3B = new THREE.Vector3();
  private static _v3C = new THREE.Vector3();
  private static _v3D = new THREE.Vector3();
  private static _quat = new THREE.Quaternion();
  private static _mat4 = new THREE.Matrix4();

  static BirthTime: number = 1;
  static PlaneGeometry: THREE.PlaneGeometry = new THREE.PlaneGeometry(1, 1, 1, 1);

  /**
   * Trail spawning (spaceType 1): each tick adds world-space movement distance to `_trailAccum`.
   * Spawn batch: `floor(_trailAccum * birthRate * TRAIL_BIRTH_SCALE)`; remainder stays in `_trailAccum`.
   * Units: world units along the path; `birthRate` scales how many ribbon samples per unit distance.
   * Tune here if trail density vs. vanilla differs (single knob).
   */
  private static readonly TRAIL_BIRTH_SCALE = 1.0;

  /** Hard cap on linked ribbon samples (each sample = 2 verts). Avoids huge allocations from extreme MDL values. */
  private static readonly LINKED_MAX_SAMPLES = 512;

  /** Minimum linked ribbon samples when sizing the buffer (see getMaxParticleCount). */
  private static readonly LINKED_MIN_SAMPLES = 32;

  /** Set to false to silence `[emitterName]` tick logs (see `emitterLog`). */
  static EMITTER_TICK_DEBUG = false;

  updateType: string;
  maxParticleCount: number = 0;

  //Geometry BufferAttributes
  offsets: THREE.BufferAttribute|THREE.InstancedBufferAttribute;
  velocities: THREE.BufferAttribute|THREE.InstancedBufferAttribute;
  props: THREE.BufferAttribute|THREE.InstancedBufferAttribute;
  ids: THREE.BufferAttribute|THREE.InstancedBufferAttribute;

  particleCount: number = 0;
  referenceNode: OdysseyObject3D = new OdysseyObject3D();

  // Lightning properties
  lightningZigZag: number = 0;
  lightningScale: number = 0;
  lightningDelay: number = 0;
  lightningRadius: number = 0;
  lightningSubDiv: number = 0;
  _lightningDelay: number = 0;

  // Velocity calculation cache
  d: number = 0;
  vx: number = 0;
  vy: number = 0;
  vz: number = 0;
  d2: number = 0;

  context: any;
  lifeExp: number = -1;
  _birthTimer: number = 0;

  birthRate: number = 0;
  birthRateRandom: number = 0;

  velocity: number = 0;
  randVelocity: number = 0;
  drag: number = 0;
  mass: number = 0;
  spread: number = 0;
  gravity: number = 0;
  threshold: number = 0;

  size: THREE.Vector3 = new THREE.Vector3();
  isDetonated: boolean = false;
  /** Linked ribbon: ring write head (0..N-1). Non-linked: linear spawn index. */
  particleIndex: number = 0;
  /** Linked: total spawns ever (for active vertex count before ring fills buffer). */
  linkedTotalSpawns: number = 0;
  vec3: THREE.Vector3 = new THREE.Vector3();
  sizeXY: THREE.Vector2 = new THREE.Vector2();
  node: OdysseyModelNodeEmitter;
  geometry: THREE.BufferGeometry;
  material: THREE.ShaderMaterial;

  colorStart: THREE.Color = new THREE.Color(1, 1, 1);
  colorMid: THREE.Color = new THREE.Color(1, 1, 1);
  colorEnd: THREE.Color = new THREE.Color(1, 1, 1);

  sizes: [number, number, number] = [0, 0, 0];
  sizesY: [number, number, number] = [0, 0, 0];
  opacity: [number, number, number] = [1, 1, 1];
  angle: number = 0;
  _detonate: number = 0;
  fps: number = 0;
  blurLength: number = 0;
  bounceCo: number = 0;
  combineTime: number = 0;
  p2pBezier2: number = 0;
  p2pBezier3: number = 0;
  percentStart: number = 0;
  percentMid: number = 0;
  percentEnd: number = 0;
  targetSize: number = 0;
  controlPTCount: number = 0;
  controlPTDelay: number = 0;
  tangentSpread: number = 0;
  tangentLength: number = 0;
  controlPTRadius: number = 0;

  speed_min: number = 0;
  speed_max: number = 0;
  xangle: number = 0;
  zangle: number = 0;
  //@ts-ignore
  mesh: THREE.Points|THREE.Mesh;
  speed: number = 0;

  // Inherit tracking: emitter world position delta between frames
  private _lastWorldPos: THREE.Vector3 = new THREE.Vector3();
  private _worldPosDelta: THREE.Vector3 = new THREE.Vector3();
  private _hasLastWorldPos: boolean = false;

  // Trail spawn tracking (spawntype 1)
  private _lastTrailPos: THREE.Vector3 = new THREE.Vector3();
  private _hasLastTrailPos: boolean = false;
  private _trailAccum: number = 0;

  /** Previous ribbon sample center (world) for tangent along linked trail */
  private _lastRibbonCenter: THREE.Vector3 = new THREE.Vector3();
  private _hasRibbonCenter: boolean = false;

  // Chunk emitter state
  private _chunkGeometry: THREE.BufferGeometry = null;
  private _chunkMaterial: THREE.Material = null;
  private _chunkLoaded: boolean = false;
  private _isChunkEmitter: boolean = false;
  private _chunkMesh: THREE.InstancedMesh = null;
  private _chunkMatrix: THREE.Matrix4 = new THREE.Matrix4();

  constructor(odysseyNode: OdysseyModelNode){
    super();
    (this as any).type = 'OdysseyEmitter';

    this.node = odysseyNode as OdysseyModelNodeEmitter;
    this._isChunkEmitter = !!(this.node.chunkResRef && this.node.chunkResRef.length > 0);

    switch(this.node.renderMode){
      case 'Normal':
      case 'Motion_Blur':
      case 'Linked':
        this.geometry = new THREE.BufferGeometry();
      break;
      default:
        this.geometry = new THREE.InstancedBufferGeometry();
        this.geometry.index = OdysseyEmitter3D.PlaneGeometry.index;
        this.geometry.attributes.position = OdysseyEmitter3D.PlaneGeometry.attributes.position;
        this.geometry.attributes.uv = OdysseyEmitter3D.PlaneGeometry.attributes.uv;
      break;
    }

    this.addEventListener( 'added', () => {
      if(!this.material || !this.parent) return;
      this.material.uniforms.matrix.value.copy(this.parent.matrix);
      this.material.uniforms.matrix.value.setPosition(0, 0, 0);
      this.material.uniformsNeedUpdate = true;
      this.attributeChanged('mass');
    });

    if(odysseyNode instanceof OdysseyModelNode){

      this.updateType = this.node.updateMode;

      this.material = new THREE.ShaderMaterial({
        uniforms: THREE.UniformsUtils.merge( [
          THREE.ShaderLib['odyssey-emitter'].uniforms, {
            textureAnimation: { value: new THREE.Vector4(
              this.node.gridX || 1,
              this.node.gridY || 1,
              (this.node.gridX || 1) * (this.node.gridY || 1),
              this.node.loop ?? 1
            )},
          }
        ]),
        vertexShader: THREE.ShaderLib['odyssey-emitter'].vertexShader,
        fragmentShader: THREE.ShaderLib['odyssey-emitter'].fragmentShader,
        side: THREE.FrontSide,
        transparent: true,
        fog: false,
        visible: true
      });

      if(this.node.twoSidedTex || this.node.renderMode == 'Linked' || this.node.renderMode == 'Billboard_to_Local_Z'){
        this.material.side = THREE.DoubleSide;
      }

      if(!this._isChunkEmitter){
        TextureLoader.enQueueParticle(this.node.textureResRef, this);
      }

      this.node.controllers.forEach( (controller: OdysseyController ) => {
        if(controller.data.length){
          switch(controller.type){
            case OdysseyModelControllerType.Position:
            break;
            case OdysseyModelControllerType.Orientation:
            break;
            case OdysseyModelControllerType.ColorStart:
              this.colorStart.r = controller.data[0].x;
              this.colorStart.g = controller.data[0].y;
              this.colorStart.b = controller.data[0].z;
            break;
            case OdysseyModelControllerType.ColorMid:
              this.colorMid.r = controller.data[0].x;
              this.colorMid.g = controller.data[0].y;
              this.colorMid.b = controller.data[0].z;
            break;
            case OdysseyModelControllerType.ColorEnd:
              this.colorEnd.r = controller.data[0].x;
              this.colorEnd.g = controller.data[0].y;
              this.colorEnd.b = controller.data[0].z;
            break;
            case OdysseyModelControllerType.XSize:
              this.size.x = controller.data[0].value * 0.01;
            break;
            case OdysseyModelControllerType.YSize:
              this.size.y = controller.data[0].value * 0.01;
            break;
            case OdysseyModelControllerType.Spread:
              this.spread = controller.data[0].value;
            break;
            case OdysseyModelControllerType.LifeExp:
              this.lifeExp = controller.data[0].value >= 0 ? controller.data[0].value : -1;
            break;
            case OdysseyModelControllerType.BirthRate:
              this.birthRate = controller.data[0].value;
            break;
            case OdysseyModelControllerType.Drag:
              this.drag = controller.data[0].value;
            break;
            case OdysseyModelControllerType.Threshold:
              this.threshold = controller.data[0].value;
            break;
            case OdysseyModelControllerType.Gravity:
              this.gravity = controller.data[0].value;
            break;
            case OdysseyModelControllerType.Mass:
              this.mass = controller.data[0].value;
            break;
            case OdysseyModelControllerType.Velocity:
              this.velocity = controller.data[0].value;
            break;
            case OdysseyModelControllerType.RandomVelocity:
              this.randVelocity = controller.data[0].value;
            break;
            case OdysseyModelControllerType.SizeStart:
              this.sizes[0] = controller.data[0].value;
            break;
            case OdysseyModelControllerType.SizeMid:
              this.sizes[1] = controller.data[0].value;
            break;
            case OdysseyModelControllerType.SizeEnd:
              this.sizes[2] = controller.data[0].value;
            break;
            case OdysseyModelControllerType.SizeStart_Y:
              this.sizesY[0] = controller.data[0].value;
            break;
            case OdysseyModelControllerType.SizeMid_Y:
              this.sizesY[1] = controller.data[0].value;
            break;
            case OdysseyModelControllerType.SizeEnd_Y:
              this.sizesY[2] = controller.data[0].value;
            break;
            case OdysseyModelControllerType.AlphaStart:
              this.opacity[0] = controller.data[0].value;
            break;
            case OdysseyModelControllerType.AlphaMid:
              this.opacity[1] = controller.data[0].value;
            break;
            case OdysseyModelControllerType.AlphaEnd:
              this.opacity[2] = controller.data[0].value;
            break;
            case OdysseyModelControllerType.ParticleRot:
              // Radians per second (matches shipped KotOR assets; NWN-style "deg/s" would feel ~57× too slow here).
              this.angle = controller.data[0]?.value ?? 0;
            break;
            case OdysseyModelControllerType.Detonate:
              this._detonate = controller.data[0].value;
            break;
            case OdysseyModelControllerType.FPS:
              this.fps = controller.data[0].value;
            break;
            case OdysseyModelControllerType.FrameStart:
              this.material.uniforms.frameRange.value.x = controller.data[0].value;
            break;
            case OdysseyModelControllerType.FrameEnd:
              this.material.uniforms.frameRange.value.y = controller.data[0].value;
            break;
            case OdysseyModelControllerType.LightningZigZag:
              this.lightningZigZag = controller.data[0].value;
            break;
            case OdysseyModelControllerType.LightningDelay:
              this.lightningDelay = controller.data[0].value;
            break;
            case OdysseyModelControllerType.LightningRadius:
              this.lightningRadius = controller.data[0].value;
            break;
            case OdysseyModelControllerType.LightningSubDiv:
              this.lightningSubDiv = controller.data[0].value;
            break;
            case OdysseyModelControllerType.LightningScale:
              this.lightningScale = controller.data[0].value;
            break;
            case OdysseyModelControllerType.Bounce_Co:
              this.bounceCo = controller.data[0].value;
            break;
            case OdysseyModelControllerType.BlurLength:
              this.blurLength = controller.data[0].value;
            break;
            case OdysseyModelControllerType.CombineTime:
              this.combineTime = controller.data[0].value;
            break;
            case OdysseyModelControllerType.P2P_Bezier2:
              this.p2pBezier2 = controller.data[0].value;
            break;
            case OdysseyModelControllerType.P2P_Bezier3:
              this.p2pBezier3 = controller.data[0].value;
            break;
            case OdysseyModelControllerType.RandomBirthRate:
              this.birthRateRandom = controller.data[0].value;
            break;
            case OdysseyModelControllerType.PercentStart:
              this.percentStart = controller.data[0].value;
            break;
            case OdysseyModelControllerType.PercentMid:
              this.percentMid = controller.data[0].value;
            break;
            case OdysseyModelControllerType.PercentEnd:
              this.percentEnd = controller.data[0].value;
            break;
            case OdysseyModelControllerType.TargetSize:
              this.targetSize = controller.data[0].value;
            break;
            case OdysseyModelControllerType.ControlPTCount:
              this.controlPTCount = controller.data[0].value;
            break;
            case OdysseyModelControllerType.ControlPTRadius:
              this.controlPTRadius = controller.data[0].value;
            break;
            case OdysseyModelControllerType.ControlPTDelay:
              this.controlPTDelay = controller.data[0].value;
            break;
            case OdysseyModelControllerType.TangentSpread:
              this.tangentSpread = controller.data[0].value;
            break;
            case OdysseyModelControllerType.TangentLength:
              this.tangentLength = controller.data[0].value;
            break;
          }
        }
      });

      this.material.uniforms.maxAge.value = (this.lifeExp >= 0 ? this.lifeExp : -1);
      this.material.uniforms.colorStart.value.copy(this.colorStart);
      this.material.uniforms.colorMid.value.copy(this.colorMid);
      this.material.uniforms.colorEnd.value.copy(this.colorEnd);
      this.material.uniforms.opacity.value.fromArray(this.opacity);
      this.material.uniforms.scale.value.fromArray(this.sizes);
      this.material.uniforms.scaleY.value.fromArray(this.sizesY);
      this.material.uniforms.rotate.value = this.angle;
      this.material.uniforms.drag.value = this.drag;
      this.material.uniforms.velocity.value = this.velocity;
      this.material.uniforms.randVelocity.value = this.randVelocity;
      this.material.uniforms.blurLength.value = this.blurLength;

      this.maxParticleCount = this.getMaxParticleCount();

      if(this.node.updateMode == 'Lightning'){
        this.material.defines.LIGHTNING = '';
      }

      if(this.fps){
        this.material.defines.FPS = '';
        this.material.uniforms.fps.value = this.fps;
      }

      if(this.node.flags.isRandom){
        this.material.defines.RANDOM_PLAYBACK = '';
      }

      if(this.node.flags.isTinted){
        this.material.defines.TINTED = '';
      }

      // DEPTH_TEXTURE is deferred to tick() when context (and its depth target) is available

      if(this.node.deadSpace > 0 && this.node.renderMode == 'Aligned_to_Particle_Dir'){
        this.material.defines.DEADSPACE = '';
        this.material.uniforms.deadSpace.value = this.node.deadSpace;
      }

      this.material.defines[this.node.renderMode] = '';

      this._birthTimer = this.birthRate > 0 ? 1/this.birthRate : 1;

      switch(this.node.blendMode){
        case 'Normal':
          this.material.blending = THREE.NormalBlending;
        break;
        case 'Lighten':
          this.material.blending = THREE.AdditiveBlending;
          this.material.transparent = false;
          this.material.depthWrite = false;
          this.material.needsUpdate = true;
        break;
        case 'Punch-Through':
          this.material.blending = THREE.NormalBlending;
          this.material.alphaTest = 0.5;
          this.material.needsUpdate = true;
        break;
      }

      const offsets: number[] = [];
      const props: number[] = [];
      const velocities: number[] = [];
      const ids: number[] = [];

      //Start Velocity Calculations
      this.speed_min = this.velocity;
      this.speed_max = this.randVelocity;

      this.xangle = this.spread;
      this.zangle = this.spread;
      this.vx = Math.sin(this.xangle);
      this.vy = Math.sin(this.zangle);
      this.vz = Math.cos(this.xangle) + Math.cos(this.zangle);

      const vSum = Math.abs(this.vx) + Math.abs(this.vy) + Math.abs(this.vz);
      this.d = vSum > 0 ? this.speed_min / vSum : 0;
      this.d2 = vSum > 0 ? this.speed_max / vSum : 0;
      //End Velocity Calculations

      switch(this.node.renderMode){
        case 'Normal':
        case 'Motion_Blur':
          this.material.defines.POINTS = '';

          this.offsets = new THREE.BufferAttribute( new Float32Array( offsets ), 3 ).setUsage( THREE.DynamicDrawUsage );
          this.velocities = new THREE.BufferAttribute( new Float32Array( velocities ), 4 ).setUsage( THREE.DynamicDrawUsage );
          this.props = new THREE.BufferAttribute( new Float32Array( props ), 4 ).setUsage( THREE.DynamicDrawUsage );
          this.ids = new THREE.InstancedBufferAttribute( new Float32Array( ids ), 1 ).setUsage( THREE.DynamicDrawUsage );
          this.geometry.setAttribute( 'position', this.offsets );
          this.geometry.setAttribute( 'velocity', this.velocities );
          this.geometry.setAttribute( 'props', this.props );
          this.geometry.setAttribute( 'ids', this.ids );
          
          this.mesh = new THREE.Points( this.geometry, this.material );
        break;
        case 'Linked':
          this.material.defines.LINKED = '';

          this.offsets = new THREE.BufferAttribute( new Float32Array( offsets ), 3 ).setUsage( THREE.DynamicDrawUsage );
          this.velocities = new THREE.BufferAttribute( new Float32Array( velocities ), 4 ).setUsage( THREE.DynamicDrawUsage );
          this.props = new THREE.BufferAttribute( new Float32Array( props ), 4 ).setUsage( THREE.DynamicDrawUsage );
          this.ids = new THREE.BufferAttribute( new Float32Array( ids ), 1 ).setUsage( THREE.DynamicDrawUsage );
          this.geometry.setAttribute( 'position', this.offsets );
          this.geometry.setAttribute( 'offset', this.velocities );
          this.geometry.setAttribute( 'props', this.props );
          this.geometry.setAttribute( 'ids', this.ids );
          
          this.mesh = new THREE.Mesh( this.geometry, this.material );
        break;
        default:
          this.offsets = new THREE.InstancedBufferAttribute( new Float32Array( offsets ), 3 ).setUsage( THREE.DynamicDrawUsage );
          this.velocities = new THREE.InstancedBufferAttribute( new Float32Array( velocities ), 4 ).setUsage( THREE.DynamicDrawUsage );
          this.props = new THREE.InstancedBufferAttribute( new Float32Array( props ), 4 ).setUsage( THREE.DynamicDrawUsage );
          this.ids = new THREE.InstancedBufferAttribute( new Float32Array( ids ), 1 ).setUsage( THREE.DynamicDrawUsage );
          this.geometry.setAttribute( 'offset', this.offsets );
          this.geometry.setAttribute( 'velocity', this.velocities );
          this.geometry.setAttribute( 'props', this.props );
          this.geometry.setAttribute( 'ids', this.ids );
      
          this.mesh = new THREE.Mesh( this.geometry, this.material );
        break;
      }

      this.mesh.renderOrder = this.node.renderOrder || 0;
      this.mesh.frustumCulled = false;
      this.material.uniformsNeedUpdate = true;
      this.add(this.mesh);

      if(this._isChunkEmitter){
        this.loadChunkModel();
      }
    }
  }

  getMaxParticleCount(){
    if(this.node.renderMode == 'Linked'){
      if(this.updateType == 'Lightning'){
        return 10 * 2;
      }else{
        // Each logical sample = 2 verts (L/R). `le`/`br` use ceil so fractional MDL values size conservatively.
        const le = Math.max(1, Math.ceil(this.lifeExp >= 0 ? this.lifeExp : 1));
        const br = Math.max(1, Math.ceil(this.birthRate || 0));
        const samples = Math.min(
          OdysseyEmitter3D.LINKED_MAX_SAMPLES,
          Math.max(OdysseyEmitter3D.LINKED_MIN_SAMPLES, le * br * 2)
        );
        return samples * 2;
      }
    }else{
      return (Math.ceil( (this.lifeExp >= 0 ? this.lifeExp : 1) ) * Math.ceil(this.birthRate));
    }
  }

  getRandomPosition(){
    const spread = OdysseyEmitter3D._v3A.copy(this.size);
    const quat = OdysseyEmitter3D._quat.set(0, 0, 0, 1);
    
    if(this.node.renderMode == 'Normal' || this.node.renderMode == 'Motion_Blur'){
      const pos = OdysseyEmitter3D._v3B;
      this.getWorldQuaternion(quat);
      this.getWorldPosition(pos);
      return new THREE.Vector3(
        ( Math.random() * spread.x - ( spread.x * 0.5 ) ),
        ( Math.random() * spread.y - ( spread.y * 0.5 ) ),
        ( Math.random() * spread.z - ( spread.z * 0.5 ) )
      ).applyQuaternion(this.parent.quaternion).add(pos);
    }else if(this.node.renderMode == 'Linked'){
      this.getWorldQuaternion(quat);
      const pos = new THREE.Vector3();
      this.getWorldPosition(pos);
      return pos;
    }
    return new THREE.Vector3(
      this.position.x + ( Math.random() * spread.x - ( spread.x * 0.5 ) ),
      this.position.y + ( Math.random() * spread.y - ( spread.y * 0.5 ) ),
      this.position.z + ( Math.random() * spread.z - ( spread.z * 0.5 ) )
    ).applyQuaternion(this.quaternion);
  }

  /** Returns center + random offset in [-range/2, +range/2] */
  randomFloat(center: number, range: number){
    return center + ( Math.random() * range - ( range * 0.5 ) );
  }

  getRandomMaxAge(){
    return this.lifeExp;
  }

  /** Console helper: every line starts with `[node.name]` for multi-emitter captures. */
  private emitterLog(label: string, data?: Record<string, unknown>){
    if(!OdysseyEmitter3D.EMITTER_TICK_DEBUG) return;
    const tag = `[${this.node?.name ?? 'OdysseyEmitter'}]`;
    if(data !== undefined){
      console.log(tag, label, data);
    }else{
      console.log(tag, label);
    }
  }

  /** After `spawnParticle`, log buffer attribute snapshot for the spawned slot (debug). */
  private logSpawnParticleAttributes(slotIndex: number, delta: number){
    if(!OdysseyEmitter3D.EMITTER_TICK_DEBUG) return;
    if(this.node.renderMode === 'Linked'){
      const base = slotIndex * 2;
      const propAt = (vi: number) => {
        const p = base + vi;
        return {
          age: this.props.getX(p),
          maxAge: this.props.getY(p),
          alive: this.props.getZ(p),
          w: this.props.getW(p),
        };
      };
      const posAt = (vi: number) => {
        const p = base + vi;
        return { x: this.offsets.getX(p), y: this.offsets.getY(p), z: this.offsets.getZ(p) };
      };
      const velAt = (vi: number) => {
        const p = base + vi;
        return {
          x: this.velocities.getX(p),
          y: this.velocities.getY(p),
          z: this.velocities.getZ(p),
          w: this.velocities.getW(p),
        };
      };
      let uvL = { x: 0, y: 0 };
      let uvR = { x: 0, y: 0 };
      const uvAttr = this.geometry.getAttribute('uv') as THREE.BufferAttribute | undefined;
      if(uvAttr && uvAttr.count > base + 1){
        uvL = { x: uvAttr.getX(base), y: uvAttr.getY(base) };
        uvR = { x: uvAttr.getX(base + 1), y: uvAttr.getY(base + 1) };
      }
      this.emitterLog('spawn', {
        ringSlot: slotIndex,
        delta,
        positionLeft: posAt(0),
        positionRight: posAt(1),
        centerApprox: {
          x: (this.offsets.getX(base) + this.offsets.getX(base + 1)) * 0.5,
          y: (this.offsets.getY(base) + this.offsets.getY(base + 1)) * 0.5,
          z: (this.offsets.getZ(base) + this.offsets.getZ(base + 1)) * 0.5,
        },
        propsLeft: propAt(0),
        propsRight: propAt(1),
        velocityLeft: velAt(0),
        velocityRight: velAt(1),
        uvLeft: uvL,
        uvRight: uvR,
        idLeft: this.ids.getX(base),
        idRight: this.ids.getX(base + 1),
      });
    }else{
      const i = slotIndex;
      let uv = { x: 0, y: 0 };
      const uvAttr = this.geometry.getAttribute('uv') as THREE.BufferAttribute | undefined;
      if(uvAttr && uvAttr.count > i){
        uv = { x: uvAttr.getX(i), y: uvAttr.getY(i) };
      }
      this.emitterLog('spawn', {
        particleIndex: i,
        delta,
        position: { x: this.offsets.getX(i), y: this.offsets.getY(i), z: this.offsets.getZ(i) },
        props: {
          age: this.props.getX(i),
          maxAge: this.props.getY(i),
          alive: this.props.getZ(i),
          w: this.props.getW(i),
        },
        velocity: {
          x: this.velocities.getX(i),
          y: this.velocities.getY(i),
          z: this.velocities.getZ(i),
          w: this.velocities.getW(i),
        },
        uv,
        id: this.ids.getX(i),
      });
    }
  }

  //Update the emitter
  tick(delta: number = 0){

    if(!delta){
      delta = 1/30;
    }

    if ( this.parent === null ) {
      this.matrixWorld.copy( this.matrix );
    } else {
      this.matrixWorld.multiplyMatrices( this.parent.matrixWorld, this.matrix );
    }

    // Track emitter world position delta for inherit/trail
    const currentWorldPos = OdysseyEmitter3D._v3C;
    this.getWorldPosition(currentWorldPos);
    if(this._hasLastWorldPos){
      this._worldPosDelta.subVectors(currentWorldPos, this._lastWorldPos);
    }else{
      this._worldPosDelta.set(0, 0, 0);
    }
    this._lastWorldPos.copy(currentWorldPos);
    this._hasLastWorldPos = true;

    // Tinting: update tint color from scene ambient if isTinted
    if(this.node.flags.isTinted && this.context){
      const ambient = this.context?.module?.area?.sun?.ambientColor || this.context?.ambientColor;
      if(ambient){
        this.material.uniforms.tintColor.value.copy(ambient);
      }
    }

    // Soft-particle (DEPTH_TEXTURE) is disabled until resolution tracking and
    // depth-buffer plumbing are fully wired up in the renderer.

    let updatePositions = false;
    let updateVelocity = false;
    let updateProperties = false;

    this.material.uniforms.time.value += delta;
    this._birthTimer -= delta;
    if(this._birthTimer < 0)
      this._birthTimer = 0;

    let maxParticleCount = this.getMaxParticleCount();
    let resizeArrays = (maxParticleCount > this.offsets.count);
    this.maxParticleCount = maxParticleCount;

    if(resizeArrays){
      let offsets = new Float32Array(this.maxParticleCount * 3);
      let velocities = new Float32Array(this.maxParticleCount * 4);
      let props = new Float32Array(this.maxParticleCount * 4);
      let ids = new Float32Array(this.maxParticleCount * 1);

      offsets.set(this.offsets.array);
      if(this.node.renderMode != 'Linked')
        velocities.set(this.velocities.array);
      props.set(this.props.array);

      switch(this.node.renderMode){
        case 'Normal':
        case 'Motion_Blur':
          this.offsets = new THREE.BufferAttribute( offsets, 3 ).setUsage( THREE.DynamicDrawUsage );
          this.velocities = new THREE.BufferAttribute( velocities, 4 ).setUsage( THREE.DynamicDrawUsage );
          this.props = new THREE.BufferAttribute( props, 4 ).setUsage( THREE.DynamicDrawUsage );
          this.ids = new THREE.InstancedBufferAttribute( ids, 1 ).setUsage( THREE.DynamicDrawUsage );
          this.geometry.setAttribute( 'position', this.offsets );
          this.geometry.setAttribute( 'velocity', this.velocities );
          this.geometry.setAttribute( 'props', this.props );
          this.geometry.setAttribute( 'ids', this.ids );
        break;
        case 'Linked':
          this.offsets = new THREE.BufferAttribute( offsets, 3 ).setUsage( THREE.DynamicDrawUsage );
          this.velocities = new THREE.BufferAttribute( velocities, 4 ).setUsage( THREE.DynamicDrawUsage );
          this.props = new THREE.BufferAttribute( props, 4 ).setUsage( THREE.DynamicDrawUsage );
          this.ids = new THREE.BufferAttribute( ids, 1 ).setUsage( THREE.DynamicDrawUsage );
          this.geometry.setAttribute( 'position', this.offsets );
          this.geometry.setAttribute( 'offset', this.velocities );
          this.geometry.setAttribute( 'props', this.props );
          this.geometry.setAttribute( 'ids', this.ids );
        break;
        default:
          this.offsets = new THREE.InstancedBufferAttribute( offsets, 3 ).setUsage( THREE.DynamicDrawUsage );
          this.velocities = new THREE.InstancedBufferAttribute( velocities, 4 ).setUsage( THREE.DynamicDrawUsage );
          this.props = new THREE.InstancedBufferAttribute( props, 4 ).setUsage( THREE.DynamicDrawUsage );
          this.ids = new THREE.InstancedBufferAttribute( ids, 1 ).setUsage( THREE.DynamicDrawUsage );
          this.geometry.setAttribute( 'offset', this.offsets );
          this.geometry.setAttribute( 'velocity', this.velocities );
          this.geometry.setAttribute( 'props', this.props );
          this.geometry.setAttribute( 'ids', this.ids );
        break;
      }

      updatePositions = true;
      if(this.node.renderMode != 'Linked')
        updateVelocity = true;
      updateProperties = true;
      if(this.node.renderMode === 'Linked'){
        this.particleIndex = 0;
        this.linkedTotalSpawns = 0;
      }
      this.emitterLog('bufferResize', {
        maxParticleCount: this.maxParticleCount,
        newVertCount: this.offsets.count,
        renderMode: this.node.renderMode,
      });
    }

    if(this.updateType == 'Lightning'){
      this.emitterLog('tick:earlyReturn', { reason: 'Lightning', delta });
      this.tickLightning(delta);
      return;
    }

    // P2P: attractor / Bezier paths (per-particle indices). Linked ribbons use main tick below
    // (trail + ring + strip); P2PGravity indexes every *vertex* and moves offsets — wrong for L/R pairs.
    // KotOR MDL can set P2P flags on Linked trail emitters (e.g. v_heal_imp); those must not early-return here.
    if(this.node.flags.isP2P && this.node.renderMode !== 'Linked' && this.node.renderMode !== 'Fountain'){
      this.emitterLog('tick:earlyReturn', { reason: this.node.flags.isP2PSel ? 'P2PBezier' : 'P2PGravity', delta });
      if(this.node.flags.isP2PSel){
        this.tickP2PBezier(delta);
      }else{
        this.tickP2PGravity(delta);
      }
      // if(this.node.renderMode !== 'Linked' && this.node.flags.affectedByWind){
      //   this.applyWindForces(delta);
      // }
      return;
    }

    // Trail spawn type (MDL spawntype 1): distance-based birthing — no spawns the first frame we only record baseline
    let trailBirthCount = 0;
    let trailDistThisFrame = 0;
    if(this.node.spaceType === 1){
      if(this._hasLastTrailPos){
        const dist = currentWorldPos.distanceTo(this._lastTrailPos);
        trailDistThisFrame = dist;
        this._trailAccum += dist;
        const rate = this.birthRate * OdysseyEmitter3D.TRAIL_BIRTH_SCALE;
        trailBirthCount = rate > 0
          ? Math.floor(this._trailAccum * rate)
          : 0;
        if(trailBirthCount > 0 && rate > 0){
          this._trailAccum -= trailBirthCount / rate;
        }
      }
      this._lastTrailPos.copy(currentWorldPos);
      this._hasLastTrailPos = true;
    }

    let birthCount = 0;
    let spawnableParticleCount = this.offsets.count;

    const attrPerVertex = 1;

    if(this.node.renderMode == 'Linked'){
      const av = 2;
      spawnableParticleCount = Math.floor(this.offsets.count / av) || 0;

      // Age drives shader alpha via positionInTime; recycling dead slots is by ring overwrite on spawn (no props.z flip).
      for(let i = 0; i < spawnableParticleCount; i++){
        let age = this.props.getX(i * av) || 0;
        const maxAge = this.props.getY(i * av) || this.lifeExp;
        const alive = this.props.getZ(i * av) == 1;

        if(alive){
          if(maxAge >= 0.0 && age >= maxAge){
            age = maxAge;
          }else{
            age += delta;
          }
        }else{
          age = 0;
        }

        this.props.setX(i * av, age || 0);
        updateProperties = true;

        for(let pI = 1; pI < av; pI++){
          this.props.setX((i * av) + pI, this.props.getX(i * av));
          this.props.setY((i * av) + pI, this.props.getY(i * av));
          this.props.setZ((i * av) + pI, this.props.getZ(i * av));
          this.props.setW((i * av) + pI, this.props.getW(i * av));
        }
      }

      let toSpawn = 0;
      if(this.node.spaceType === 1 && trailBirthCount > 0){
        toSpawn = Math.min(trailBirthCount, spawnableParticleCount);
      }else if(this.birthRate > 0 && !this._birthTimer){
        toSpawn = 1;
      }

      for(let s = 0; s < toSpawn; s++){
        if(spawnableParticleCount <= 0) break;
        const idx = this.particleIndex % spawnableParticleCount;
        this.spawnParticle(idx, delta);
        this.particleIndex = (this.particleIndex + 1) % spawnableParticleCount;
        this.linkedTotalSpawns++;
        updatePositions = true;
        updateVelocity = true;
      }

      const logicalDraw = Math.min(this.linkedTotalSpawns, spawnableParticleCount);
      const activeVertCount = logicalDraw * av;
      this.rebuildLinkedRibbonIndexBuffer(activeVertCount);

      if(logicalDraw > 0 && activeVertCount < this.offsets.count){
        const lastWritten = (this.particleIndex + spawnableParticleCount - 1) % spawnableParticleCount;
        const lastBase = lastWritten * av;
        for(let v = activeVertCount; v < this.offsets.count; v++){
          const src = lastBase + (v % av);
          this.offsets.setX(v, this.offsets.getX(src));
          this.offsets.setY(v, this.offsets.getY(src));
          this.offsets.setZ(v, this.offsets.getZ(src));
          this.velocities.setX(v, this.velocities.getX(src));
          this.velocities.setY(v, this.velocities.getY(src));
          this.velocities.setZ(v, this.velocities.getZ(src));
          this.velocities.setW(v, this.velocities.getW(src));
        }
      }

      this.emitterLog('linkedTick', {
        delta,
        worldPos: { x: currentWorldPos.x, y: currentWorldPos.y, z: currentWorldPos.z },
        spaceType: this.node.spaceType,
        updateMode: this.node.updateMode,
        trailDistThisFrame,
        trailBirthCount,
        trailAccum: this._trailAccum,
        hasLastTrailPos: this._hasLastTrailPos,
        birthRate: this.birthRate,
        trailBirthScale: OdysseyEmitter3D.TRAIL_BIRTH_SCALE,
        birthTimer: this._birthTimer,
        toSpawn,
        particleIndex: this.particleIndex,
        linkedTotalSpawns: this.linkedTotalSpawns,
        spawnableParticleCount,
        logicalDraw,
        activeVertCount,
        lifeExp: this.lifeExp,
        vertCount: this.offsets.count,
        maxParticleCount: this.maxParticleCount,
        hasRibbonCenter: this._hasRibbonCenter,
      });
    }

    if(this.node.renderMode != 'Linked'){
    for(let i = 0; i < spawnableParticleCount; i++){

        let age = this.props.getX(i) || 0;
        let maxAge = this.props.getY(i) || (this.lifeExp >= 0 ? this.lifeExp : -1);
        let alive = this.props.getZ(i) == 1;

        if(i < this.maxParticleCount){

          if(this.node.updateMode == 'Single'){
            age += delta;
            this.props.setX(i, age || 0);
            // Keep per-particle maxAge (infinite = -1); do not force 1.0 or static Single breaks.
            this.props.setY(i, maxAge);
            this.props.setZ(i, 1);
            updateProperties = true;
          }else{
            if(alive){
              if(maxAge >= 0.0 && age >= maxAge){
                age = 0;
                this.particleCount -= 1;
                this.props.setZ(i, 0);
              }else{
                age += delta;
                if(maxAge > 0.0 && age > maxAge) age = maxAge;
              }

              // Bounce: check ground collision for alive particles
              if(this.node.flags.canBounce && this.node.renderMode !== 'Linked'){
                this.checkBounce(i);
              }
            }else{

              let canSpawn = !this._birthTimer;
              let maxSpawn = 1;

              if(this.node.updateMode == 'Explosion'){
                canSpawn = this.isDetonated;
                maxSpawn = this.birthRate;
              }

              // Trail mode overrides the normal birth logic
              if(this.node.spaceType === 1){
                canSpawn = trailBirthCount > 0;
                maxSpawn = Math.max(1, trailBirthCount);
              }

              if(canSpawn && birthCount < maxSpawn){
                this.spawnParticle(i, delta);
                updatePositions = true;
                if(this.node.renderMode != 'Linked')
                  updateVelocity = true;
                birthCount++;
              }

              age = 0;
            }
          }

          this.props.setX(i, age || 0);
          updateProperties = true;

        }else{
          if(this.node.updateMode != 'Single'){
            if(alive){
              if(maxAge >= 0.0 && age >= maxAge){
                age = 0;
                this.particleCount -= 1;
                this.props.setZ(i, 0);
              }else{
                age += delta;
              }
            }else{
              age = 0;
            }
            this.props.setX(i, age || 0);
            updateProperties = true;
          }
        }

      }

    }

    // Inherit: move POINTS-mode particles with emitter
    if(this.node.renderMode === 'Normal' && this.node.flags.canInherit && this._hasLastWorldPos){
      for(let i = 0; i < spawnableParticleCount; i++){
        if(this.props.getZ(i) === 1){
          this.offsets.setX(i, this.offsets.getX(i) + this._worldPosDelta.x);
          this.offsets.setY(i, this.offsets.getY(i) + this._worldPosDelta.y);
          this.offsets.setZ(i, this.offsets.getZ(i) + this._worldPosDelta.z);
        }
      }
      updatePositions = true;
    }

    // InheritLocal: keep instanced-mode particles at their world-space spawn position
    if(this.node.renderMode !== 'Normal' && this.node.renderMode !== 'Linked' &&
       this.node.flags.canInheritLocal && this._hasLastWorldPos){
      const invQuat = OdysseyEmitter3D._quat.copy(this.quaternion).invert();
      const localDelta = OdysseyEmitter3D._v3A.copy(this._worldPosDelta).applyQuaternion(invQuat);
      for(let i = 0; i < spawnableParticleCount; i++){
        if(this.props.getZ(i) === 1){
          this.offsets.setX(i, this.offsets.getX(i) - localDelta.x);
          this.offsets.setY(i, this.offsets.getY(i) - localDelta.y);
          this.offsets.setZ(i, this.offsets.getZ(i) - localDelta.z);
        }
      }
      updatePositions = true;
    }

    // Wind: impulses (blast) + area wind (ARE WindPower / Forge preview); same resolver as dangly.
    // Linked ribbon: world-space verts are frozen at spawn — never push them with wind.
    if(this.node.renderMode !== 'Linked' && this.node.flags.affectedByWind){
      this.applyWindForces(delta);
      updatePositions = true;
    }

    if(this.node.renderMode == "Aligned_to_Particle_Dir" && this.parent){
      this.material.uniforms.matrix.value.copy(this.parent.matrix);
      this.material.uniforms.matrix.value.setPosition(0, 0, 0);
      this.material.uniformsNeedUpdate = true;
    }

    if(updatePositions)
      this.offsets.needsUpdate = true;

    if(updateVelocity)
      this.velocities.needsUpdate = true;

    if(updateProperties)
      this.props.needsUpdate = true;

    if(!this._birthTimer){
      this._birthTimer = this.birthRate > 0 ? 1/this.birthRate : 1;
    }

    if(this.geometry.boundingSphere)
      this.geometry.boundingSphere.radius = ( (this.size.length() || 1) + Math.abs(this.velocity) + Math.abs(this.randVelocity) + Math.abs(this.drag) + Math.abs(this.mass)  ) * (Math.max.apply(null, this.sizes) * 2);

    if(this.node.renderMode !== 'Linked'){
      this.emitterLog('tick', {
        renderMode: this.node.renderMode,
        delta,
        spaceType: this.node.spaceType,
        updateMode: this.node.updateMode,
        birthRate: this.birthRate,
        birthTimer: this._birthTimer,
        lifeExp: this.lifeExp,
        particleCount: this.particleCount,
        offsetsCount: this.offsets.count,
        maxParticleCount: this.maxParticleCount,
      });
    }

    this.isDetonated = false;

    this.sortParticles();

    // Update chunk instances if this is a chunk emitter
    if(this._isChunkEmitter && this._chunkLoaded){
      this.updateChunkInstances();
    }
  }

  setReferenceNode( referenceNode: OdysseyObject3D ){
    if(referenceNode instanceof OdysseyObject3D){
      this.referenceNode = referenceNode;
    }
  }

  tickLightning(delta: number = 0){
    if(this._lightningDelay === undefined){
      this._lightningDelay = 0.00;
    }

    let lightningZigZag = this.lightningZigZag + 1;
    const start = OdysseyEmitter3D._v3A.set(0, 0, 0);
    this.getWorldPosition(start);
    const target = OdysseyEmitter3D._v3B.set(0, 0, 0);
    this.referenceNode.getWorldPosition(target);

    let scale = 0.5;

    let gridX1 = 2;
    let indices: number[] = [];
    let vertices: number[] = [];
    let normals: number[] = [];
    let uvs: number[] = [];
    let velocities: number[] = [];
    let props: number[] = [];
    let spread = (this.lightningScale || 0);
    let age = 0;

    if(this._lightningDelay >= this.lightningDelay){
      if(this.parent && (this.parent.quaternion.x || this.parent.quaternion.y || this.parent.quaternion.z || this.parent.quaternion.w != 1))
        this.parent.quaternion.set(0, 0, 0, 1);

      this._lightningDelay = 0;
      for(let iy = 0; iy < lightningZigZag; iy++){
        let percentage = iy/lightningZigZag;
        let x = start.x + ( (target.x - start.x) * percentage);
        let y = start.y + ( (target.y - start.y) * percentage);
        let z = start.z + ( (target.z - start.z) * percentage);

        if(iy === 0){
          // First vertex: keep at start position
        }else if(iy + 1 === lightningZigZag){
          x = this.randomFloat(x, this.lightningRadius);
          y = this.randomFloat(y, this.lightningRadius);
          z = this.randomFloat(z, this.lightningRadius);
        }else{
          x = this.randomFloat(x, spread);
          y = this.randomFloat(y, spread);
          z = this.randomFloat(z, spread);
        }

        for ( let ix = 0; ix < 2; ix ++ ) {
          let xO = scale/2;
          if(ix == 1){
            xO = -scale/2;
          }

          vertices.push( x + xO, - y, z );
          normals.push( 0, 0, 1 );
          uvs.push( ix / 1 );
          uvs.push( 1 - ( iy / (lightningZigZag-1) ) );

          velocities.push(0, 0, 0, 0);

          if(!this.geometry.attributes.props){
            age = 0;
          }else{
            age = ((this.geometry.attributes.props as THREE.BufferAttribute).getX( 0 ) || 0) + delta;
          }

          if(age >= 1)
            age = 0;

          props.push(age, 1, 1, 0);
        }
      }

      // Index and geometry building AFTER the vertex loop
      for(let iy2 = 0; iy2 < lightningZigZag-1; iy2++){
        for ( let ix = 0; ix < 1; ix++ ) {
          let a = ix + gridX1 * iy2;
          let b = ix + gridX1 * ( iy2 + 1 );
          let c = ( ix + 1 ) + gridX1 * ( iy2 + 1 );
          let d = ( ix + 1 ) + gridX1 * iy2;
          indices.push( a, b, d );
          indices.push( b, c, d );
        }
      }

      this.geometry.setIndex(indices);
      this.geometry.setAttribute( 'position', new THREE.Float32BufferAttribute( vertices, 3 ) );
      this.geometry.setAttribute( 'offset', new THREE.Float32BufferAttribute( velocities, 4 ) );
      this.geometry.setAttribute( 'props', new THREE.Float32BufferAttribute( props, 4 ) );
      this.geometry.setAttribute( 'normal', new THREE.Float32BufferAttribute( normals, 3 ) );
      this.geometry.setAttribute( 'uv', new THREE.Float32BufferAttribute( uvs, 2 ) );

      // Update references to newly created attributes
      this.velocities = this.geometry.getAttribute('offset') as THREE.BufferAttribute;
      this.props = this.geometry.getAttribute('props') as THREE.BufferAttribute;

      if(this.geometry.boundingSphere)
        this.geometry.boundingSphere.radius = start.distanceTo(target);

    }else{
      this._lightningDelay += delta;
      for(let iy = 0; iy < lightningZigZag; iy++){
        for ( let ix = 0; ix < 2; ix++ ) {
          if(!this.geometry.attributes.props){
            age = 0;
          }else{
            age = ((this.geometry.attributes.props as THREE.BufferAttribute).getX( 0 ) || 0) + delta;
          }

          if(age >= 1)
            age = 0;

          props.push(age, 1, 1, 0);
        }
      }
      this.geometry.setAttribute( 'props', new THREE.Float32BufferAttribute( props, 4 ) );
      this.props = this.geometry.getAttribute('props') as THREE.BufferAttribute;
    }
  }

  /**
   * P2P Gravity mode: particles are attracted toward the reference node.
   * Gravity accelerates particles toward the target, drag allows overshoot,
   * and threshold kills particles within a radius of the target.
   */
  tickP2PGravity(delta: number){
    let updatePositions = false;
    let updateVelocity = false;
    let updateProperties = false;

    // Target in world space (offsets are emitter-local; mixing with world ref was wrong and looked like pull toward origin).
    const refW = OdysseyEmitter3D._v3A;
    this.referenceNode.getWorldPosition(refW);

    const pW = OdysseyEmitter3D._v3B;
    const toRef = OdysseyEmitter3D._v3C;
    const velW = OdysseyEmitter3D._v3D;

    let birthCount = 0;
    const spawnableParticleCount = this.offsets.count;

    for(let i = 0; i < spawnableParticleCount; i++){
      let age = this.props.getX(i) || 0;
      let maxAge = this.props.getY(i) || (this.lifeExp >= 0 ? this.lifeExp : -1);
      let alive = this.props.getZ(i) == 1;

      if(alive){
        const px = this.offsets.getX(i);
        const py = this.offsets.getY(i);
        const pz = this.offsets.getZ(i);

        pW.set(px, py, pz);
        this.localToWorld(pW);

        toRef.copy(refW).sub(pW);
        const dist = toRef.length();

        // Kill particle if within threshold distance
        if(this.threshold > 0 && dist < this.threshold){
          this.props.setZ(i, 0);
          this.particleCount -= 1;
          age = 0;
        }else if(maxAge >= 0.0 && age >= maxAge){
          this.props.setZ(i, 0);
          this.particleCount -= 1;
          age = 0;
        }else{
          if(dist > 0.001) toRef.multiplyScalar(1.0 / dist);

          velW.set(
            this.velocities.getX(i),
            this.velocities.getY(i),
            this.velocities.getZ(i)
          );

          velW.addScaledVector(toRef, this.gravity * delta);

          const dampFactor = 1.0 / (1.0 + this.drag);
          velW.multiplyScalar(1.0 - dampFactor * delta);

          this.velocities.setX(i, velW.x);
          this.velocities.setY(i, velW.y);
          this.velocities.setZ(i, velW.z);

          pW.addScaledVector(velW, delta);
          this.worldToLocal(pW);

          this.offsets.setX(i, pW.x);
          this.offsets.setY(i, pW.y);
          this.offsets.setZ(i, pW.z);

          age += delta;
          updatePositions = true;
          updateVelocity = true;
        }
      }else{
        let canSpawn = !this._birthTimer;
        let maxSpawn = 1;
        if(this.node.updateMode == 'Explosion'){
          canSpawn = this.isDetonated;
          maxSpawn = this.birthRate;
        }
        if(canSpawn && birthCount < maxSpawn){
          this.spawnParticle(i, delta);
          updatePositions = true;
          updateVelocity = true;
          birthCount++;
        }
        age = 0;
      }
      this.props.setX(i, age || 0);
      updateProperties = true;
    }

    if(updatePositions) this.offsets.needsUpdate = true;
    if(updateVelocity) this.velocities.needsUpdate = true;
    if(updateProperties) this.props.needsUpdate = true;

    if(!this._birthTimer){
      this._birthTimer = this.birthRate > 0 ? 1/this.birthRate : 1;
    }

    this.isDetonated = false;
    this.sortParticles();
  }

  /**
   * P2P Bezier mode: particles travel along a cubic Bezier curve from
   * the emitter to the reference node. combineTime controls the transition
   * duration. p2pBezier2/3 control source/target tangent lengths.
   */
  tickP2PBezier(delta: number){
    let updatePositions = false;
    let updateProperties = false;

    const startPos = OdysseyEmitter3D._v3A.set(0, 0, 0);
    this.getWorldPosition(startPos);
    const endPos = OdysseyEmitter3D._v3B.set(0, 0, 0);
    this.referenceNode.getWorldPosition(endPos);

    // Build control points along the emitter's local Z axis
    const forward = OdysseyEmitter3D._v3C.set(0, 0, 1);
    const quat = OdysseyEmitter3D._quat;
    this.getWorldQuaternion(quat);
    forward.applyQuaternion(quat);

    const cp1 = new THREE.Vector3().copy(startPos).addScaledVector(forward, this.p2pBezier2);
    const cp2 = new THREE.Vector3().copy(endPos).addScaledVector(forward, -this.p2pBezier3);

    const curve = new THREE.CubicBezierCurve3(startPos.clone(), cp1, cp2, endPos.clone());
    const transitTime = this.combineTime > 0 ? this.combineTime : 1;

    let birthCount = 0;
    const spawnableParticleCount = this.offsets.count;

    for(let i = 0; i < spawnableParticleCount; i++){
      let age = this.props.getX(i) || 0;
      let maxAge = this.props.getY(i) || (this.lifeExp >= 0 ? this.lifeExp : -1);
      let alive = this.props.getZ(i) == 1;

      if(alive){
        if(maxAge >= 0.0 && age >= maxAge){
          this.props.setZ(i, 0);
          this.particleCount -= 1;
          age = 0;
        }else{
          age += delta;
          const t = Math.min(age / transitTime, 1.0);
          const pointW = curve.getPoint(t);
          const localP = OdysseyEmitter3D._v3D;
          localP.copy(pointW);
          this.worldToLocal(localP);

          this.offsets.setX(i, localP.x);
          this.offsets.setY(i, localP.y);
          this.offsets.setZ(i, localP.z);
          updatePositions = true;
        }
      }else{
        let canSpawn = !this._birthTimer;
        let maxSpawn = 1;
        if(this.node.updateMode == 'Explosion'){
          canSpawn = this.isDetonated;
          maxSpawn = this.birthRate;
        }
        if(canSpawn && birthCount < maxSpawn){
          this.spawnParticle(i, delta);
          updatePositions = true;
          birthCount++;
        }
        age = 0;
      }
      this.props.setX(i, age || 0);
      updateProperties = true;
    }

    if(updatePositions) this.offsets.needsUpdate = true;
    if(updateProperties) this.props.needsUpdate = true;

    if(!this._birthTimer){
      this._birthTimer = this.birthRate > 0 ? 1/this.birthRate : 1;
    }

    this.isDetonated = false;
    this.sortParticles();
  }

  /**
   * Linked mode: ribbon — two world-space vertices per sample (left, right) in strip order
   * L0,R0,L1,R1,… so triangle-strip indices connect consecutive segments.
   */
  setLinkedVertexPosition(logicalIndex = 0, position = new THREE.Vector3){
    const base = logicalIndex * 2;
    const center = position;
    const tangent = OdysseyEmitter3D._v3A;
    if(this._hasRibbonCenter){
      tangent.subVectors(center, this._lastRibbonCenter);
    }else if(this._hasLastWorldPos && this._worldPosDelta.lengthSq() > 1e-10){
      tangent.copy(this._worldPosDelta);
    }else{
      tangent.set(0, 0, -1);
    }
    if(tangent.lengthSq() < 1e-10){
      tangent.set(0, 1, 0);
    }else{
      tangent.normalize();
    }

    const toCam = OdysseyEmitter3D._v3B;
    const cam = this.context?.currentCamera as THREE.Camera | undefined;
    if(cam){
      toCam.subVectors(cam.position, center);
    }else{
      toCam.set(0, 0, 1);
    }
    const side = OdysseyEmitter3D._v3D.crossVectors(tangent, toCam);
    if(side.lengthSq() < 1e-10){
      side.crossVectors(tangent, new THREE.Vector3(0, 1, 0));
    }
    side.normalize();

    const avgScale = Math.max(
      this.sizes[0],
      this.sizes[1],
      this.sizes[2],
      this.size.length() || 0,
      0.15
    );
    const halfW = Math.max(0.05, avgScale * 0.5);

    const left = OdysseyEmitter3D._v3C.copy(center).addScaledVector(side, -halfW);
    const right = OdysseyEmitter3D._v3D.copy(center).addScaledVector(side, halfW);

    this._lastRibbonCenter.copy(center);
    this._hasRibbonCenter = true;

    this.ensureLinkedUvCapacity();
    const uvAttr = this.geometry.getAttribute('uv') as THREE.BufferAttribute;
    const tUv = (logicalIndex * 0.0625) % 1.0;

    this.offsets.setXYZ(base, left.x, left.y, left.z);
    this.offsets.setXYZ(base + 1, right.x, right.y, right.z);
    this.velocities.setXYZW(base, 0, 0, 0, this.mass);
    this.velocities.setXYZW(base + 1, 0, 0, 0, this.mass);
    uvAttr.setXY(base, 0, tUv);
    uvAttr.setXY(base + 1, 1, tUv);
    uvAttr.needsUpdate = true;
  }

  private ensureLinkedUvCapacity(){
    if(this.node.renderMode !== 'Linked') return;
    const n = this.offsets.count;
    let uv = this.geometry.getAttribute('uv') as THREE.BufferAttribute | undefined;
    if(!uv || uv.count !== n){
      const arr = new Float32Array(n * 2);
      if(uv?.array?.length){
        arr.set(uv.array as ArrayLike<number>);
      }
      uv = new THREE.BufferAttribute(arr, 2).setUsage(THREE.DynamicDrawUsage);
      this.geometry.setAttribute('uv', uv);
    }
  }

  /**
   * Triangle list equivalent to a triangle strip (L,R,L,R,…) along the trail in spawn order.
   * Linked mode stores samples in a ring buffer: after wrap, buffer index order is not chronological;
   * this maps strip order to actual buffer slots from oldest sample to newest.
   */
  private rebuildLinkedRibbonIndexBuffer(activeVertCount?: number){
    const slotCount = Math.floor(this.offsets.count / 2) || 0;
    if(slotCount < 1){
      this.geometry.setIndex(null);
      return;
    }
    const cap = activeVertCount !== undefined
      ? Math.min(Math.floor(activeVertCount / 2), slotCount)
      : Math.min(this.linkedTotalSpawns, slotCount);
    const logicalDraw = Math.max(0, cap);
    const nVert = logicalDraw * 2;
    if(nVert < 3){
      this.geometry.setIndex(null);
      return;
    }
    const full = this.linkedTotalSpawns >= slotCount;
    const strip: number[] = [];
    for(let k = 0; k < logicalDraw; k++){
      const slot = full ? (this.particleIndex + k) % slotCount : k;
      const base = slot * 2;
      strip.push(base, base + 1);
    }
    const numberOfTriangles = nVert - 2;
    const indices: number[] = [];
    for(let newI = 0; newI < numberOfTriangles; newI++){
      const a = strip[newI];
      const b = strip[newI + 1];
      const c = strip[newI + 2];
      if(newI % 2 === 0){
        indices.push(a, b, c);
      }else{
        indices.push(c, b, a);
      }
    }
    const useUint32 = this.offsets.count > 65535;
    if(useUint32){
      this.geometry.setIndex(new THREE.Uint32BufferAttribute(indices, 1));
    }else{
      this.geometry.setIndex(new THREE.Uint16BufferAttribute(indices, 1));
    }
    this.geometry.clearGroups();
  }

  spawnParticle(i = 0, delta: number = 0){
    let newPosition = this.getRandomPosition();
    if(this.node.renderMode != 'Linked'){
      this.offsets.setX(i, newPosition.x);
      this.offsets.setY(i, newPosition.y);
      this.offsets.setZ(i, newPosition.z);
    }else{
      this.setLinkedVertexPosition(i, newPosition);
    }

    if(this.velocity){
      if(this.node.updateMode == 'Explosion' && this.node.renderMode != 'Linked'){
        this.velocities.setX(i, this.randomFloat(this.d * this.vx, this.spread));
        this.velocities.setY(i, this.randomFloat(this.d * this.vy, this.spread));
        this.velocities.setZ(i, this.randomFloat(this.d * this.vz, this.spread));
      }else{
        const quat = OdysseyEmitter3D._quat;
        this.getWorldQuaternion(quat);
        this.vec3.set(
          this.randomFloat(this.d * this.vx, this.d2 * this.vx),
          this.randomFloat(this.d * this.vy, this.d2 * this.vy), 
          this.randomFloat(this.d * this.vz, this.d2 * this.vz)
        ).applyQuaternion(quat);

        if(this.node.renderMode != 'Linked'){
          this.velocities.setX(i, this.vec3.x);
          this.velocities.setY(i, this.vec3.y);
          this.velocities.setZ(i, this.vec3.z);
        }
      }
    }else{
      if(this.node.renderMode != 'Linked'){
        this.velocities.setX(i, 0);
        this.velocities.setY(i, 0);
        this.velocities.setZ(i, 0);
      }
    }

    // Inherit velocity from emitter movement
    if(this.node.flags.canInheritVelocity && this._hasLastWorldPos && delta > 0){
      const emitterVel = OdysseyEmitter3D._v3A.copy(this._worldPosDelta).divideScalar(delta);
      if(this.node.renderMode != 'Linked'){
        this.velocities.setX(i, this.velocities.getX(i) + emitterVel.x);
        this.velocities.setY(i, this.velocities.getY(i) + emitterVel.y);
        this.velocities.setZ(i, this.velocities.getZ(i) + emitterVel.z);
      }
    }

    if(this.node.renderMode != 'Linked'){
      this.velocities.setW(i, this.mass);
      this.props.setY(i, this.getRandomMaxAge());
      this.props.setZ(i, 1);
      this.props.setW(i, 0); // reset splat flag
    }else{
      // Must reset age (props.x) — age runs before spawn each tick; ring overwrites would keep stale age≈maxAge otherwise.
      for(let vi = 0; vi < 2; vi++){
        const p = (i * 2) + vi;
        this.props.setX(p, 0);
        this.props.setY(p, (this.lifeExp >= 0 ? this.lifeExp : 100));
        this.props.setZ(p, 1);
      }
    }

    if(this.node.renderMode == 'Linked'){
      for(let v = 0; v < 2; v++){
        this.ids.setX(i * 2 + v, i);
      }
    }else{
      this.ids.setX(i, i);
    }
    this.logSpawnParticleAttributes(i, delta);
    this.particleCount++;
  }

  /**
   * Check and handle bounce against ground/walkmesh for a particle.
   * If the particle has crossed below the ground surface, reflect its
   * velocity Z component and snap position to the surface. If canSplat
   * is set, the particle is marked as splatted on first bounce.
   */
  private checkBounce(i: number): void {
    const z = this.offsets.getZ(i);
    let groundZ = 0;

    // Attempt walkmesh lookup through context for accurate ground height
    try {
      const area = this.context?.module?.area;
      if(area?.rooms){
        const px = this.offsets.getX(i);
        const py = this.offsets.getY(i);
        const testPos = OdysseyEmitter3D._v3A.set(px, py, z);
        for(let r = 0; r < area.rooms.length; r++){
          const wm = area.rooms[r].collisionManager?.walkmesh;
          if(!wm?.walkableFaces) continue;
          for(let f = 0; f < wm.walkableFaces.length; f++){
            const face = wm.walkableFaces[f];
            if(face.triangle.containsPoint(testPos)){
              face.triangle.closestPointToPoint(testPos, OdysseyEmitter3D._v3B);
              groundZ = OdysseyEmitter3D._v3B.z;
              break;
            }
          }
          if(groundZ !== 0) break;
        }
      }
    } catch(_) {
      // No walkmesh available; use z=0
    }

    if(z <= groundZ){
      this.offsets.setZ(i, groundZ + 0.005);

      const vz = this.velocities.getZ(i);
      this.velocities.setZ(i, Math.abs(vz) * this.bounceCo);

      // Splat: mark particle as face-up on ground
      if(this.node.flags.canSplat && this.props.getW(i) !== 1){
        this.props.setW(i, 1);
      }
    }
  }

  /**
   * Blast impulses (emitter-generated) plus area wind using {@link resolveDanglyWindPower}
   * and the same world wind axis as dangly (`ODYSSEY_AREA_WIND_DIRECTION`), transformed to
   * emitter-local offsets.
   */
  private applyWindForces(delta: number): void {
    const windStrength = resolveDanglyWindPower(this.context) * 0.01;
    const wm = GameState.windManager;
    const hasImpulses = wm && wm.pointSourceWinds.length > 0;
    if(!hasImpulses && windStrength <= 1e-6){
      return;
    }

    let ax = 0;
    let ay = 0;
    let az = 0;
    if(windStrength > 1e-6){
      const w = OdysseyEmitter3D._v3A;
      w.copy(ODYSSEY_AREA_WIND_DIRECTION).multiplyScalar(windStrength * delta);
      OdysseyEmitter3D._mat4.copy(this.matrixWorld).invert();
      w.transformDirection(OdysseyEmitter3D._mat4);
      ax = w.x;
      ay = w.y;
      az = w.z;
    }

    const count = this.offsets.count;
    for(let i = 0; i < count; i++){
      if(this.props.getZ(i) !== 1) continue;

      const px = this.offsets.getX(i);
      const py = this.offsets.getY(i);
      const pz = this.offsets.getZ(i);

      if(wm && wm.pointSourceWinds.length > 0){
        const worldPos = OdysseyEmitter3D._v3B.set(px, py, pz).applyMatrix4(this.matrixWorld);
        const gust = wm.getGlobalPointWind(worldPos);
        if(gust.lengthSq() > 1e-12){
          const localGust = OdysseyEmitter3D._v3C.copy(gust);
          localGust.transformDirection(OdysseyEmitter3D._mat4);
          this.offsets.setX(i, px + localGust.x);
          this.offsets.setY(i, py + localGust.y);
          this.offsets.setZ(i, pz + localGust.z);
        }
      }

      if(windStrength > 1e-6){
        this.offsets.setX(i, this.offsets.getX(i) + ax);
        this.offsets.setY(i, this.offsets.getY(i) + ay);
        this.offsets.setZ(i, this.offsets.getZ(i) + az);
      }
    }
  }

  //https://github.com/mrdoob/three.js/blob/master/examples/webgl_custom_attributes_points2.html#L173
  sortParticles(){

    // if(!(this.mesh instanceof THREE.Points))
    //   return;

    if(this.node.renderMode == 'Linked')
      return;

    // Additive blending is order-independent; skip sorting
    if(this.material.blending === THREE.AdditiveBlending)
      return;

    if(!this.context || !this.context.currentCamera){
      return;
    }

    const vector = OdysseyEmitter3D._v3A;
    const matrix = OdysseyEmitter3D._mat4;
    matrix.multiplyMatrices( this.context.currentCamera.projectionMatrix, this.context.currentCamera.matrixWorldInverse );
    matrix.multiply( this.mesh.matrixWorld );

    let index = this.geometry.getIndex();
    let positions = (this.geometry.getAttribute( 'position' ) as THREE.BufferAttribute).array;
    let length = positions.length / 3;
    if ( index === null ) {
      let array = new Uint16Array( length );
      for ( let i = 0; i < length; i ++ ) {
        array[ i ] = i;
      }
      index = new THREE.BufferAttribute( array, 1 );
      this.geometry.setIndex( index );
    }

    let sortArray: [number, number][] = [];
    for ( let i = 0; i < length; i ++ ) {
      vector.fromArray( positions as Float32Array, i * 3 );
      vector.applyMatrix4( matrix );
      sortArray.push( [ vector.z, i ] );
    }

    sortArray.sort( (a, b) => b[ 0 ] - a[ 0 ] );
    let indices = index.array;
    for ( let i = 0; i < length; i ++ ) {
      (indices as any)[ i ] = sortArray[ i ][ 1 ];
    }
    
    this.geometry.index.needsUpdate = true;
    
  }

  /** Clears trail distance state (spawntype 1) so the next effect does not get one huge first segment. */
  private resetTrailSpawnState(){
    this._hasLastTrailPos = false;
    this._trailAccum = 0;
  }

  /** Clears linked ribbon tangent continuity (world-space L/R) after detonate/disable. */
  private resetRibbonTangentState(){
    this._hasRibbonCenter = false;
  }

  detonate(){
    this.isDetonated = true;
    this.resetTrailSpawnState();
    if(this.node.renderMode == 'Linked'){
      this.particleIndex = 0;
      this.linkedTotalSpawns = 0;
      this.resetRibbonTangentState();
    }
    let spawnableParticleCount = this.offsets.count;
    for(let i = 0; i < spawnableParticleCount; i++){
      this.props.setX(i, 0);
    }
    this.props.needsUpdate = true;
    this.material.uniforms.time.value = 0;
    this.material.uniformsNeedUpdate = true;

    // Generate wind gust for nearby grass and dangly cloth
    if(this.node.blastRadius > 0 && this.node.blastLength > 0){
      const pos = new THREE.Vector3();
      this.getWorldPosition(pos);
      GameState.windManager?.addPointSourceWind(
        pos,
        this.node.blastRadius,
        this.node.blastLength,
        1.0
      );
    }
  }

  getBirthTimer(){
    return this.birthRate > 0 ? 1/this.birthRate : 1;
  }

  disable(){
    this.birthRate = 0;
    this.resetTrailSpawnState();
    if(this.node.renderMode === 'Linked'){
      this.resetRibbonTangentState();
    }
    if(this.material){
      this.material.visible = false;
    }
  }

  dispose(){
    this.geometry?.dispose();
    this.material?.dispose();
    if(this.mesh){
      this.remove(this.mesh);
    }
    if(this._chunkMesh){
      this._chunkMesh.dispose();
      this.remove(this._chunkMesh);
    }
    if(this._chunkGeometry){
      this._chunkGeometry.dispose();
    }
    if(this._chunkMaterial){
      this._chunkMaterial.dispose();
    }
  }

  attributeChanged(attr: string){
    const quat = OdysseyEmitter3D._quat;
    switch(attr){
      case 'mass':
        if(!this.parent) return;
        this.parent.getWorldQuaternion(quat);
        this.vec3.set(0, 0, this.mass).applyQuaternion(this.quaternion);
        this.material.uniforms.mass.value.copy(this.vec3);
        this.material.uniformsNeedUpdate = true;
      break;
    }
  }

  /**
   * Load the chunk MDL model asynchronously for chunk emitters.
   * Once loaded, creates an InstancedMesh for rendering chunk particles.
   */
  private async loadChunkModel(): Promise<void> {
    try {
      const { MDLLoader } = await import("@/loaders/MDLLoader");
      const { OdysseyModel3D } = await import("@/three/odyssey/OdysseyModel3D");

      const mdl = await MDLLoader.loader.load(this.node.chunkResRef);
      if(!mdl) return;

      const model = await OdysseyModel3D.FromMDL(mdl, { context: this.context });
      if(!model) return;

      // Find the first renderable mesh geometry in the loaded model
      let foundGeom: THREE.BufferGeometry = null;
      let foundMat: THREE.Material = null;
      model.traverse((child: THREE.Object3D) => {
        if(foundGeom) return;
        if(child instanceof THREE.Mesh && child.geometry){
          foundGeom = child.geometry.clone();
          foundMat = (child.material as THREE.Material).clone();
        }
      });

      if(!foundGeom) return;

      this._chunkGeometry = foundGeom;
      this._chunkMaterial = foundMat || new THREE.MeshBasicMaterial({ color: 0xffffff });
      this._chunkMesh = new THREE.InstancedMesh(
        this._chunkGeometry,
        this._chunkMaterial,
        Math.max(this.maxParticleCount, 1)
      );
      this._chunkMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this._chunkMesh.renderOrder = this.node.renderOrder || 0;
      this._chunkMesh.frustumCulled = false;
      this._chunkLoaded = true;

      // Hide the sprite mesh for chunk emitters
      if(this.mesh){
        this.mesh.visible = false;
      }
      this.add(this._chunkMesh);

    } catch(e) {
      console.warn('OdysseyEmitter3D: Failed to load chunk model:', this.node.chunkResRef, e);
    }
  }

  /**
   * Update InstancedMesh transforms for chunk emitter particles.
   * Each alive particle gets a matrix from its offset position,
   * rotation (particleRot * age), and lifecycle scale.
   */
  private updateChunkInstances(): void {
    if(!this._chunkMesh) return;

    const mat = this._chunkMatrix;
    const count = Math.min(this.offsets.count, this._chunkMesh.count);
    let visibleCount = 0;

    for(let i = 0; i < count; i++){
      const alive = this.props.getZ(i) === 1;
      if(!alive){
        // Hide dead particles by zeroing scale
        mat.makeScale(0, 0, 0);
        this._chunkMesh.setMatrixAt(i, mat);
        continue;
      }

      const age = this.props.getX(i);
      const maxAge = this.props.getY(i) || 1;
      const positionInTime = maxAge > 0 ? age / maxAge : 0;

      // Interpolate scale over lifetime
      const t = positionInTime * 2;
      let scaleVal: number;
      if(t <= 1){
        scaleVal = this.sizes[0] + (this.sizes[1] - this.sizes[0]) * t;
      }else{
        scaleVal = this.sizes[1] + (this.sizes[2] - this.sizes[1]) * (t - 1);
      }
      if(scaleVal <= 0) scaleVal = 0.001;

      const px = this.offsets.getX(i);
      const py = this.offsets.getY(i);
      const pz = this.offsets.getZ(i);
      const rot = this.angle * age;

      mat.makeRotationZ(rot);
      mat.scale(OdysseyEmitter3D._v3A.set(scaleVal, scaleVal, scaleVal));
      mat.setPosition(px, py, pz);

      this._chunkMesh.setMatrixAt(i, mat);
      visibleCount++;
    }

    this._chunkMesh.count = count;
    this._chunkMesh.instanceMatrix.needsUpdate = true;
  }

}
